/**
 * Intake completion logic — extracted from Intake.tsx so it's unit-testable and
 * the "can I finish?" gate has one definition.
 *
 * Two distinct notions, deliberately separate:
 *   - The **gate** (`isIntakeComplete` / `incompleteRequiredSections`): can the
 *     consultant finalize? True once every REQUIRED field is filled. Recommended
 *     and optional fields never block it. This mirrors the backend's
 *     `schema.incomplete_required_intake` (field-level), so the FE and backend
 *     can't disagree (the bug where the FE enabled "Finish" but the backend
 *     409'd, or vice-versa).
 *   - The **indicator** (`sectionCompletion`): the sidebar dot/badge. A section
 *     reads "complete" once its meaningful fields (required + recommended) are
 *     all filled — purely-optional fields don't keep it from going green.
 */
import type { FieldDef, SectionDef } from './intakeSchema'

export type SectionValues = Record<string, unknown>

/** A captured value counts as filled when non-empty. Mirrors backend `schema.value_filled`. */
export function isFilled(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (typeof v === 'string') return v.trim() !== ''
  if (Array.isArray(v)) return v.length > 0
  if (typeof v === 'object') return Object.keys(v as object).length > 0
  return true
}

const isRequired = (f: FieldDef) => f.priority === 'required'
const isMeaningful = (f: FieldDef) => f.priority === 'required' || f.priority === 'recommended'

/** Required-field progress for a section (drives the finish gate + red badge). */
export function sectionRequiredStats(
  section: SectionDef,
  values: SectionValues | undefined,
): { filledRequired: number; totalRequired: number } {
  const required = section.fields.filter(isRequired)
  const filled = required.filter((f) => isFilled(values?.[f.key])).length
  return { filledRequired: filled, totalRequired: required.length }
}

/** Overall field progress (filled / total across all of a section's fields). */
export function sectionFieldStats(
  section: SectionDef,
  values: SectionValues | undefined,
): { filled: number; total: number } {
  const filled = section.fields.filter((f) => isFilled(values?.[f.key])).length
  return { filled, total: section.fields.length }
}

/**
 * Sidebar status. 'complete' once the meaningful (required + recommended) fields
 * are all filled — so a section the consultant has genuinely finished shows
 * green even if some purely-optional fields are blank. If a section has no
 * required/recommended fields, "complete" falls back to all fields filled.
 */
export function sectionCompletion(
  section: SectionDef,
  values: SectionValues | undefined,
): 'empty' | 'partial' | 'complete' {
  const anyFilled = section.fields.some((f) => isFilled(values?.[f.key]))
  if (!anyFilled) return 'empty'
  const meaningful = section.fields.filter(isMeaningful)
  const target = meaningful.length > 0 ? meaningful : section.fields
  const targetFilled = target.filter((f) => isFilled(values?.[f.key])).length
  return targetFilled === target.length ? 'complete' : 'partial'
}

/**
 * Sections still missing a required field — the finish gate. Empty array means
 * intake can be finalized. Sections with no required fields never appear here.
 */
export function incompleteRequiredSections(
  sections: readonly SectionDef[],
  captured: Record<string, SectionValues> | undefined,
): SectionDef[] {
  const cf = captured ?? {}
  return sections.filter((s) => {
    const stats = sectionRequiredStats(s, cf[s.key])
    return stats.totalRequired > 0 && stats.filledRequired < stats.totalRequired
  })
}

/** True when every required field across the given sections is filled. */
export function isIntakeComplete(
  sections: readonly SectionDef[],
  captured: Record<string, SectionValues> | undefined,
): boolean {
  return incompleteRequiredSections(sections, captured).length === 0
}
